import { useMemo } from 'react';
import { useColorScheme } from 'react-native';
import { useSelector } from 'react-redux';
import { getTheme } from '../theme';
import { getWhitelabelPalette } from '../theme/getWhitelabelPalette';
import type { ThemePreference } from '../store/reducers/session';
import type { RootState } from '../store';

export const resolveThemeMode = (preference: ThemePreference | undefined, systemScheme: 'light' | 'dark' | null): 'light' | 'dark' => (
  preference === 'system' || !preference ? (systemScheme === 'dark' ? 'dark' : 'light') : preference
);

// Theme + whitelabel resolution.
//
// Returns the base theme overlaid with the active whitelabel palette when
// either of these is set in redux:
//
//   1. state.partners.partner            — when the signed-in user IS a
//                                          partner (carries brand_primary /
//                                          brand_secondary / level directly
//                                          on the partners row).
//   2. state.session.account.whitelabel  — when a SPONSORED CUSTOMER is
//                                          signed in (computed server-side
//                                          in /users/get-user via
//                                          resolvePartnerForCustomer; same
//                                          keys, nested under whitelabel).
//   3. state.session.whitelabelOverride  — PRE-AUTH host branding, resolved
//                                          from `<subdomain>.eleplan.com` at
//                                          boot (hydrateBrandingFromHost).
//                                          Lowest precedence so a signed-in
//                                          partner/customer's real branding
//                                          always wins; this only paints the
//                                          login/register screens before auth.
//
// Partner-side wins when both happen to be set. getWhitelabelPalette is
// idempotent and no-ops when there are no brand colors configured, so
// the non-whitelabel case is unchanged.
//
// Lifting this into the hook (vs. requiring every screen to call
// getWhitelabelPalette manually) means brand colors apply across the
// whole app — not just the partner-portal screens — without a wider
// refactor of every consumer.
export const useTheme = () => {
  const themePreference = useSelector((s: RootState) => s.session.theme) as ThemePreference;
  const systemScheme = useColorScheme();
  const themeMode = resolveThemeMode(themePreference, systemScheme);
  const partner = useSelector((s: RootState) => s.partners.partner);
  const customerWhitelabel = useSelector(
    (s: RootState) => (s.session as any).account?.whitelabel,
  );
  const hostOverride = useSelector((s: RootState) => s.session.whitelabelOverride);

  return useMemo(() => {
    const base = getTheme(themeMode || 'light');
    const brand = partner
      ? {
          level: (partner as any)?.level,
          brand_primary: (partner as any)?.brand_primary,
          brand_secondary: (partner as any)?.brand_secondary,
        }
      : customerWhitelabel
        ? {
            level: customerWhitelabel.level,
            brand_primary: customerWhitelabel.brand_primary,
            brand_secondary: customerWhitelabel.brand_secondary,
          }
        : hostOverride
          ? {
              level: hostOverride.level ?? undefined,
              brand_primary: hostOverride.brand_primary,
              brand_secondary: hostOverride.brand_secondary,
            }
          : null;
    return getWhitelabelPalette(base, brand);
  }, [themeMode, partner, customerWhitelabel, hostOverride]);
};
